"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "food-storage-calculator",
  "refrigerator-organization-planner",
  "energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Kimchi Refrigerator Calculator",
  category: "Specialty" as const,
  mediumDescription:
    "Size a dedicated kimchi refrigerator (kimchijango) by household size, consumption, and whether you also store other fermented foods. Two-stage temperature design: 50–59°F for active fermentation, 32–39°F for long-term storage.",
  faqs: [
    { question: "Why do I need a separate kimchi fridge?", answer: "Kimchi releases strong odors and gases during fermentation that get absorbed by other foods in a regular fridge. A dedicated kimchi refrigerator has airtight compartments, separate fermentation and storage temperature zones, and odor-control systems built in." },
    { question: "What temperature does kimchi ferment at?", answer: "Active fermentation runs at 50–59°F (10–15°C) for the first 1–2 weeks. Long-term storage and aging happens at 32–39°F (0–4°C). A kimchi fridge runs both zones simultaneously; a regular fridge can only do one." },
    { question: "Can I make kimchi without a kimchi fridge?", answer: "Yes — ferment at room temperature for 1–2 days, then move to a regular refrigerator at 35–38°F. The flavor profile and shelf life will differ from kimchi-fridge fermentation, which is colder and slower." },
    { question: "How long does kimchi last in a kimchi fridge?", answer: "Properly stored at 32–39°F, kimchi stays good for 6 months or longer, with peak flavor around 3–4 weeks. Aged kimchi (6+ months) develops deeper sourness and is excellent for stews like kimchi jjigae." },
    { question: "What's a kimjang and how big a fridge do I need?", answer: "Kimjang is the traditional autumn kimchi-making event, where Korean families prepare 20–100+ heads of cabbage at once. Households doing kimjang typically need 3–4 compartment large or extra-large kimchi fridges to store the year's supply." },
    { question: "Do kimchi fridges only store kimchi?", answer: "Most have a multi-purpose zone (39–50°F) for other fermented foods like doenjang, gochujang, miso, pickles, and aged cheese. The flexible temperature control makes them useful for any temperature-sensitive fermented or aged product." },
  ],
  relatedTools: ["refrigerator-organization-planner", "food-storage-calculator", "refrigerator-size-calculator"],
}

export function KimchiFridgeCalculatorPageClient() {
  const [householdSize, setHouseholdSize] = useState("4")
  const [consumption, setConsumption] = useState("moderate")
  const [otherFermented, setOtherFermented] = useState("yes")

  // Original calc logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const size = readCount(householdSize, { min: 1, max: 20 })
    if (size === null) return null

    let containersPerPerson = 0
    if (consumption === "light") containersPerPerson = 1
    else if (consumption === "moderate") containersPerPerson = 2
    else containersPerPerson = 3

    let containerCount = size * containersPerPerson
    if (otherFermented === "yes") containerCount += Math.ceil(size * 0.5)

    let recommendedSize = ""
    let capacity = 0
    if (containerCount <= 4) {
      recommendedSize = "Small kimchi fridge (1–2 compartments)"
      capacity = 3.5
    } else if (containerCount <= 8) {
      recommendedSize = "Medium kimchi fridge (2–3 compartments)"
      capacity = 7
    } else if (containerCount <= 12) {
      recommendedSize = "Large kimchi fridge (3–4 compartments)"
      capacity = 11
    } else {
      recommendedSize = "Extra large kimchi fridge (4+ compartments)"
      capacity = 15
    }

    return { recommendedSize, capacity, containerCount }
  }, [householdSize, consumption, otherFermented])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Two zones. One"
      heroTitleItalic="kimchijango."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~25 sec", "Two-stage fermentation design"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "50–59°F", label: "Ferment zone", note: "1–2 weeks" },
            { value: "32–39°F", label: "Storage zone", note: "Long-term" },
            { value: "2", label: "Containers/person", note: "Moderate use" },
            { value: "6+", label: "Months shelf life", note: "Aged" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "kimchi-refrigerator-calculator")}
      cta={{
        title: "Add a counter-depth secondary fridge.",
        body: "Browse counter-depth fridges that pair well with a dedicated kimchi unit — flush integration in a kitchen run with a separate kimjang refrigerator.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Household, kimchi
                <br />
                appetite, fermenteds.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                One container ≈ 1 large jar or 1 standard kimchi storage tub.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={householdSize}
                    onChange={(e) => setHouseholdSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Kimchi consumption
                  </label>
                  <select
                    value={consumption}
                    onChange={(e) => setConsumption(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="light">Light (1 container / person)</option>
                    <option value="moderate">Moderate (2 containers / person)</option>
                    <option value="heavy">Heavy (3 containers / person)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Other fermented foods
                  </label>
                  <select
                    value={otherFermented}
                    onChange={(e) => setOtherFermented(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="yes">Yes — doenjang, gochujang, miso, pickles</option>
                    <option value="no">No — kimchi only</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Adds ~50% more container space.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Recommended capacity
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {result?.capacity ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      cu ft
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendedSize ?? "Enter household and consumption."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Containers
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.containerCount ?? "—"}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Zones recommended
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {otherFermented === "yes" ? "3" : "2"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pair your kitchen"
          title="Counter-depth fridges that fit a kimchi setup"
          description="Slim profiles that pair cleanly with a dedicated kimchijango in the same kitchen."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Sized by <em className="italic text-[#ff9951]">containers</em>, not cubic feet alone.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Kimchi fridges are sold by compartment count and container capacity. We translate household size and consumption into containers, then map to a compartment tier.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Containers per person",
                  d: "Light: 1 / person. Moderate: 2 / person. Heavy: 3 / person — based on Korean household consumption norms.",
                },
                {
                  n: "02",
                  t: "Other-fermented buffer",
                  d: "Adds 50% additional container space if you also store doenjang, gochujang, miso, pickles.",
                },
                {
                  n: "03",
                  t: "Compartment tier",
                  d: "≤4 containers → small (3.5 cu ft). 5–8 → medium (7). 9–12 → large (11). 13+ → extra large (15+).",
                },
                {
                  n: "04",
                  t: "Two-stage temperature",
                  d: "Active fermentation 50–59°F for 1–2 weeks; long-term storage 32–39°F for aging up to 6+ months.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges to compare"
          description="A small undercounter unit pairs well as a third fridge for sauces, doenjang jars, and aged condiments."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
